import React, { useMemo, useState, Fragment } from 'react';
import { supplyClassesData } from '../inputs/supplyClassesData';
import { unitData, Unit } from './UnitFilter';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { CollapsibleDetails } from './CollapsibleDetails';
// Time intervals for predictions
const timeIntervals = ['Current', '24 hours', '48 hours', '72 hours', '96 hours', '120 hours'];
interface PredictionData {
  unitId: string;
  supplyClassId: string;
  predictions: number[];
}
// Generate mock prediction data with a resupply at 96 hours
const generatePredictions = () => {
  const predictions: PredictionData[] = [];
  // Generate predictions for each unit and supply class
  unitData.filter(unit => unit.id !== 'all').forEach(unit => {
    supplyClassesData.forEach(supplyClass => {
      // Generate a starting value between 75% and 95%
      const startingValue = Math.floor(Math.random() * 21) + 75;
      // Calculate decline rate (between 10-25% per 24h period)
      const declineRate = Math.floor(Math.random() * 16) + 10;
      // Generate predictions for each time interval
      const predictionValues = timeIntervals.map((interval, index) => {
        if (index === 0) {
          // Current value
          return startingValue;
        } else if (index === 4) {
          // 96 hours - resupply point
          return 100; // Full resupply
        } else if (index === 5) {
          // 120 hours - after resupply, some consumption
          return 100 - declineRate;
        } else {
          // Progressive decline for 24h, 48h, 72h
          return Math.max(0, startingValue - declineRate * index);
        }
      });
      predictions.push({
        unitId: unit.id,
        supplyClassId: supplyClass.id,
        predictions: predictionValues
      });
    });
  });
  return predictions;
};
// Get color based on percentage
const getColorClass = (percentage: number) => {
  if (percentage >= 80) return 'bg-green-100 text-green-800'; // Good
  if (percentage >= 50) return 'bg-yellow-100 text-yellow-800'; // Warning
  if (percentage >= 20) return 'bg-red-100 text-red-800'; // Critical
  return 'bg-gray-900 text-white'; // Empty/Emergency
};
// Calculate average predictions for a battalion from its companies
const calculateBattalionAverages = (allPredictions: PredictionData[], battalionId: string) => {
  const companies = unitData.filter(unit => unit.parentId === battalionId);
  const companyIds = companies.map(company => company.id);
  return supplyClassesData.map(supplyClass => {
    const companyPredictions = allPredictions.filter(p => companyIds.includes(p.unitId) && p.supplyClassId === supplyClass.id);
    const averagePredictions = companyPredictions[0].predictions.map((_, timeIndex) => {
      const sum = companyPredictions.reduce((total, current) => total + current.predictions[timeIndex], 0);
      return Math.round(sum / companyPredictions.length);
    });
    return {
      ...supplyClass,
      predictions: averagePredictions
    };
  });
};
interface SupplyPredictionTableProps {
  selectedUnitId: string;
}
export const SupplyPredictionTable: React.FC<SupplyPredictionTableProps> = ({
  selectedUnitId
}) => {
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  // Generate all prediction data
  const allPredictions = useMemo(() => generatePredictions(), []);
  // Filter and process data based on selected unit
  const displayData = useMemo(() => {
    if (selectedUnitId === 'all') {
      // Average across all units
      return supplyClassesData.map(supplyClass => {
        const relevantPredictions = allPredictions.filter(p => p.supplyClassId === supplyClass.id);
        const averagePredictions = relevantPredictions[0].predictions.map((_, timeIndex) => {
          const sum = relevantPredictions.reduce((total, current) => total + current.predictions[timeIndex], 0);
          return Math.round(sum / relevantPredictions.length);
        });
        return {
          ...supplyClass,
          predictions: averagePredictions
        };
      });
    }
    const selectedUnit = unitData.find(unit => unit.id === selectedUnitId);
    if (!selectedUnit) return [];
    if (selectedUnit.type === 'battalion') {
      // Calculate battalion averages from its companies
      return calculateBattalionAverages(allPredictions, selectedUnit.id);
    } else {
      // Company-specific data
      return supplyClassesData.map(supplyClass => {
        const prediction = allPredictions.find(p => p.unitId === selectedUnitId && p.supplyClassId === supplyClass.id);
        return {
          ...supplyClass,
          predictions: prediction?.predictions || []
        };
      });
    }
  }, [selectedUnitId, allPredictions]);
  const toggleRowExpansion = (supplyClassId: string) => {
    setExpandedRows(prev => prev.includes(supplyClassId) ? prev.filter(id => id !== supplyClassId) : [...prev, supplyClassId]);
  };
  return <div className="overflow-x-auto" data-id="element-993">
      <table className="min-w-full divide-y divide-gray-200 border" data-id="element-994">
        <thead className="bg-gray-50" data-id="element-995">
          <tr data-id="element-996">
            <th scope="col" className="w-8 px-4 py-3" data-id="element-997"></th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-998">
              Supply Class
            </th>
            {timeIntervals.map((interval, index) => <th key={interval} scope="col" className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${index === 4 ? 'bg-blue-50' : ''}`} data-id="element-999">
                {interval}
                {index === 4 && <div className="text-xs font-normal text-blue-600 normal-case" data-id="element-1000">
                    Resupply
                  </div>}
              </th>)}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-1001">
          {displayData.map(supplyClass => <Fragment key={supplyClass.id} data-id="element-1002">
              <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleRowExpansion(supplyClass.id)} data-id="element-1003">
                <td className="px-4 py-4 whitespace-nowrap" data-id="element-1004">
                  <button className="text-gray-500 hover:text-gray-700" data-id="element-1005">
                    {expandedRows.includes(supplyClass.id) ? <ChevronUpIcon size={16} data-id="element-1006" /> : <ChevronDownIcon size={16} data-id="element-1007" />}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1008">
                  <div className="flex items-center" data-id="element-1009">
                    <span className="mr-2" data-id="element-1010">{supplyClass.icon}</span>
                    <div data-id="element-1011">
                      <div className="text-sm font-medium text-gray-900" data-id="element-1012">
                        Class {supplyClass.romanNumeral}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-1013">
                        {supplyClass.name}
                      </div>
                    </div>
                  </div>
                </td>
                {supplyClass.predictions.map((prediction, index) => <td key={`${supplyClass.id}-${index}`} className={`px-6 py-4 whitespace-nowrap ${index === 4 ? 'bg-blue-50' : ''}`} data-id="element-1014">
                    <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-medium ${getColorClass(prediction)}`} data-id="element-1015">
                      {prediction}%
                    </span>
                    {index === 4 && <div className="mt-1 text-xs text-blue-600" data-id="element-1016">
                        Resupplied
                      </div>}
                  </td>)}
              </tr>
              {expandedRows.includes(supplyClass.id) && <tr data-id="element-1017">
                  <td colSpan={timeIntervals.length + 2} data-id="element-1018">
                    <CollapsibleDetails supplyClass={supplyClass} data-id="element-1019" />
                  </td>
                </tr>}
            </Fragment>)}
        </tbody>
      </table>
    </div>;
};